/**
 * THE Span renderer. One renderer, four certainty treatments, no exceptions.
 *
 * This component is the reason honesty survives a second developer: a
 * duration cannot be drawn without passing through here, and this function
 * cannot draw an UNMEASURABLE span as if it were measured. A rendering
 * CONVENTION decays the first time someone builds a component without
 * reading the design doc; a TYPE does not (design §6.1).
 *
 * Rules the renderer physically enforces:
 *   1. a total is never a bare sum across clock domains — `Total` demands a
 *      seam count and prints it;
 *   2. an unmeasurable or bounded segment is never omitted — it is drawn at
 *      its slot with `∅` / `≤` and a reason;
 *   3. a cross-clock span carries its FLOOR line, drawn behind the bar;
 *   4. Capital's `order_post_ms` and IBKR's are different Spans and never
 *      share an axis — `ackLabel` is the only place that discriminator is
 *      written down.
 */

import type { Certainty, Clock, Span } from "./api";

/** Four marks, one family, always immediately before the number, always in
 *  Falcon Grey. Attestation is a WORD, not a second glyph family — two
 *  simultaneous glyph families in phone-width cells is a private language
 *  with no legend (D17). */
export const CLOCK_MARK: Record<Clock, string> = {
  MONO: "⌂",
  OUR_WALL: "⌂",
  X_HOST: "↔",
  // U+2301 ⌁ is in NO brand face at any weight — it fell back to DejaVu at a
  // different x-height and a non-tabular advance, which broke the alignment
  // of every latency column. U+2248 ≈ is present, and "approximately" is
  // what a cross-domain span actually means.
  SRV_TO_OUR: "≈",
  OUR_TO_VENUE: "≈",
  MIXED: "≈",
};

export const CLOCK_TITLE: Record<Clock, string> = {
  MONO: "measured inside one process or one host — trustworthy",
  OUR_WALL: "our node's wall clock",
  X_HOST: "two hosts' wall clocks — NTP-dependent, can go negative",
  SRV_TO_OUR: "source server → our clock",
  OUR_TO_VENUE: "our clock → venue clock",
  MIXED: "composed across clock domains",
};

const CERTAINTY_TITLE: Record<Certainty, string> = {
  MEASURED: "measured",
  BOUNDED: "bounded, not measured — an upper bound derived from a residual",
  DERIVED: "derived — a one-sided bound, never a symmetric envelope",
  UNMEASURABLE: "structurally not derivable from the journals",
};

export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "∅";
  const neg = ms < 0;
  const a = Math.abs(ms);
  const body = a < 1000 ? `${Math.round(a)}ms` : `${(a / 1000).toFixed(2)}s`;
  // A negative cross-host span is EVIDENCE about the fleet's clocks. Never
  // clamped to zero, never hidden (design §6.2).
  return neg ? `−${body}` : body;
}

export function prefix(certainty: Certainty | null | undefined): string {
  return certainty === "BOUNDED" ? "≤" : certainty === "DERIVED" ? "≥" : "";
}

/** The one place the ack-semantics contract rule is written down:
 *  `ack := venue_ack_ms if order_post_ms_semantics present else order_post_ms`
 *  and the label changes with it. The two never share an axis. */
export function ackLabel(orderPostSemantics: string | null | undefined): string {
  return orderPostSemantics ? "socket write (IBKR)" : "venue round trip (Capital)";
}

export function SpanBar({
  span,
  scale = 1,
  label,
}: {
  span: Span | null | undefined;
  scale?: number;
  label?: string;
}) {
  if (!span) {
    return (
      <div className="span span--unmeasurable" data-certainty="UNMEASURABLE">
        {label && <span className="span__label">{label}</span>}
        <span className="span__clock">∅</span>
        <span className="span__track" style={{ width: 24 }} />
        <span className="span__num dim">∅</span>
        <span className="span__note">no such hop on this case</span>
      </div>
    );
  }
  const cls = `span span--${span.certainty.toLowerCase()}`;
  const width = span.ms === null ? 24 : Math.max(2, Math.min(220, Math.abs(span.ms) * scale));
  const floorW = span.floor_ms === null ? 0 : Math.max(1, Math.min(220, Math.abs(span.floor_ms) * scale));
  return (
    <div className={cls} data-certainty={span.certainty} data-clock={span.clock}>
      {label && <span className="span__label">{label}</span>}
      <span className="span__clock" title={CLOCK_TITLE[span.clock]}>
        {span.certainty === "UNMEASURABLE" ? "∅" : CLOCK_MARK[span.clock]}
      </span>
      <span className="span__track" style={{ width: Math.max(width, floorW) }}>
        {/* the 30 d empirical floor, drawn BEHIND the bar as a calibration
            line. Anything below it is skew, not speed. */}
        {floorW > 0 && <span className="span__floor" style={{ width: floorW }} title="30 d empirical minimum" />}
        <span className="span__bar" style={{ width }} />
      </span>
      <span className="span__num" title={CERTAINTY_TITLE[span.certainty]}>
        {prefix(span.certainty)}
        {fmtMs(span.ms)}
      </span>
      {span.certainty === "BOUNDED" && <span className="span__note">bounded, not measured</span>}
      {span.certainty === "DERIVED" && <span className="span__note">derived — one-sided</span>}
      {span.note && <span className="span__note">{span.note}</span>}
      {span.seams > 0 && (
        <span className="span__note" title={span.domains.join(" → ")}>
          {span.seams} seam{span.seams > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

/** A headline latency on a feed row: mark + number, nothing else. Still a
 *  Span in spirit — the three fields travel together and the renderer
 *  refuses to draw a number without its clock and certainty. */
export function SpanInline({
  ms,
  clock,
  certainty,
  hop,
}: {
  ms: number | null;
  clock: Clock | null;
  certainty: Certainty | null;
  hop?: string | null;
}) {
  if (ms === null || clock === null || certainty === null) {
    return (
      <span className="dim" title="no measurable hop on this case">
        —
      </span>
    );
  }
  return (
    <span className="span-inline" title={`${hop ?? ""} · ${CLOCK_TITLE[clock]}`} data-certainty={certainty}>
      <span className="span__clock">{CLOCK_MARK[clock]}</span>
      {prefix(certainty)}
      {fmtMs(ms)}
    </span>
  );
}

/** A total is NEVER a bare sum across clock domains: it is labelled with its
 *  seam count and names the domains it crosses. */
export function Total({
  label,
  ms,
  seams,
  note,
}: {
  label: string;
  ms: number | null;
  seams: number;
  note: string;
}) {
  return (
    <div className="total" data-seams={seams}>
      <span className="total__label">{label}</span>
      <span className="total__num mono">{ms === null ? "∅" : fmtMs(ms)}</span>
      <span className="span__note">
        {seams} seam{seams === 1 ? "" : "s"} · {note}
      </span>
    </div>
  );
}

/** The completeness dot strip — per hop: measured / bounded / derived /
 *  unmeasurable / absent. Five kinds of absence get five renderings (§6.7). */
export function DotStrip({ spans }: { spans: Array<Span | null | undefined> }) {
  const measured = spans.filter((s) => s && s.certainty === "MEASURED").length;
  const bounded = spans.filter((s) => s && s.certainty === "BOUNDED").length;
  return (
    <div className="dots" aria-label={`measured ${measured} of ${spans.length} hops`}>
      {spans.map((s, i) => (
        <span
          key={i}
          className={`dot dot--${(s?.certainty ?? "absent").toLowerCase()}`}
          title={s ? `${s.from_hop}: ${s.certainty}` : "absent"}
        />
      ))}
      <span className="span__note">
        measured {measured} of {spans.length} hops
        {bounded > 0 ? ` · ${bounded} bounded` : ""}
      </span>
    </div>
  );
}
